import React, { useCallback, useEffect, useMemo, useState, Dispatch, SetStateAction } from "react";
import { useApiRequest } from "../../hooks/useApiRequest";
import { Order } from "../../types/order";
import Grid from "@mui/material/Unstable_Grid2"; // Grid version 2
import {
  type OrderCardConstructionOption as OrderCardConstructionOptionType,
} from "../../types/orderCardConstructionOption";
import { OrderCardBase } from "./OrderCardBase";
import InputField from "../../components/InputField";
import SelectField, { SelectFieldProps } from "../../components/SelectField";
import {
  InputAdornment,
  Typography,
} from "@mui/material";
import { ConstructionOption, getConstructionOptionPricePerShortDisplayName } from "../../types/constructionOption";
import { useOrderCard } from "./OrderCardContext";
import { Controller, useForm } from "react-hook-form";
import { formatAsCurrency, formatWithCommas } from "../../utils/number";
import { DeepPartial } from "../../utils/deepPartial";
import { OrderCardEditResponse } from "./orderCardEditResponse";
import { OrderCard } from "../../types/orderCard";
import produce from "immer";
import { OrderCardActionButton } from "./OrderCardActionButton";
import { useGlobalToastNotificationContext } from "../../context/GlobalToastNotificationContext";
import { LoadingOverlay } from "../../components/LoadingOverlay";

let savedTimestamp = 0;

type FormData = {
  selectedOptionGuid: string | null;
  notes: string | null;
  quantity: number | null;
};

const getDefaultValues = (
  orderCard: DeepPartial<OrderCardConstructionOptionType> | null
): FormData => {
  const selectedOptionGuid = orderCard?.cardData?.selectedOptionGuid ?? "";

  return {
    selectedOptionGuid,
    notes: orderCard?.cardData?.notes ?? "",
    quantity: orderCard?.cardData?.quantity ?? null,
  };
};

interface OrderCardConstructionOptionProps {
  order: Order;
  orderCard: OrderCardConstructionOptionType;
  expanded: boolean;
  groupIndex: number;
  setOrder: Dispatch<SetStateAction<Order | null>>;
  onOrderCardEdited: (
    orderCardEditResponse: OrderCardEditResponse<OrderCard>
  ) => void;
}

export const OrderCardConstructionOption: React.FC<
  OrderCardConstructionOptionProps
> = ({ order, orderCard, expanded, groupIndex, setOrder, onOrderCardEdited }) => {
  const { setEditingOrderCard } = useOrderCard();

  const disabled = orderCard.isLocked;
  const orderCardIndex = order?.orderCards?.findIndex((dcard) => dcard.orderCardGuid === orderCard.orderCardGuid);

  const {
    control,
    register,
    reset,
    formState,
    getValues,
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: getDefaultValues(orderCard),
    reValidateMode: "onChange",
    mode: "onChange",
  });

  useEffect(() => {
    reset(getDefaultValues(orderCard), { keepDirtyValues: true });
  }, [orderCard, reset]);

  const optionsFieldOptions: SelectFieldProps["options"] = useMemo(() => {
    const options: SelectFieldProps["options"] = [
      ...orderCard.selectionOptions.map( (option) => getOptionOption(option) ),
    ];
    return options;
  }, [
    orderCard.selectionOptions,
  ]);

  const isAddCustomOptionButtonVisible = orderCard.constructionGroupGuid && !disabled;

  const orderCardTotal = useMemo(() => {
    const totalPrice = orderCard.totalPrice ?? 0;
    const totalFormatted = formatAsCurrency(totalPrice);
    return totalPrice > 0 ? `+${totalFormatted}` : totalFormatted;
  }, [orderCard.totalPrice]);

  const getOrderCardData = useCallback((
    formData: FormData
  ): OrderCardConstructionOptionType["cardData"] => {
    const { ...values } = formData;

    const cardData: OrderCardConstructionOptionType["cardData"] = {
      ...values,
    };

    return cardData;
  },[]);

  const watchedOptionGuid = watch("selectedOptionGuid");
  const selectedOption = useMemo(() => {
    if (!watchedOptionGuid) return null;
    return orderCard.selectionOptions.find((o) => o.constructionOptionGuid === watchedOptionGuid) ?? null;
  }, [watchedOptionGuid, orderCard.selectionOptions]);

  const isPricePerQuantity = selectedOption?.optionPricedPer === "q";

  // This is a subgroup option editor that doesn't nuke the rest of the card during a save event, rather it just responds
  // with a save success or failure for a single field.
  const {
    request: saveOrderCardRequest,
  } = useApiRequest<OrderCardEditResponse<OrderCard>>(false, "orderCard");


  const handleBlurField = () => {
    const cardData = getOrderCardData(getValues());
    const thisTimestamp = new Date().getTime();
    if (thisTimestamp > savedTimestamp) savedTimestamp = thisTimestamp;
    saveOrderCardRequest(`/ordercards/${orderCard.orderCardGuid}/edit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: { 'cardData': cardData },
    }, {
      onSuccess: (data) => {
        if (savedTimestamp === thisTimestamp) {
          onOrderCardEdited(data);
          if (orderCardIndex) {
            setOrder((prevOrder) =>
              produce(prevOrder, (draft) => {
                if (!draft || !draft.orderCards) return;
                draft.orderCards[orderCardIndex] = data.orderCard;
              })
            );
          }
        }
      },
      onUnsuccess() {
        //
      } 
    });
  };

  const handleEditingField = () => {
    setEditingOrderCard(orderCard);
  };

  const handleClearClick = () => {
    const defaultValues = getDefaultValues(null);
    reset(defaultValues);
    handleBlurField();
  };

  const {
      data: addCustomOptionData,
      status: addCustomOptionStatus,
      loading: addCustomOptionLoading,
      request: addCustomOptionRequest,
      errorMessage: addCustomOptionErrorMessage,
    } = useApiRequest<any>();

  const [isLoading, setIsLoading] = useState(false);
  const handleAddCustomOptionClick = () => {
    setIsLoading(true);
    addCustomOptionRequest(`/orders/${order.orderGuid}/constructionsubgroups/add`, {
      method: "POST",
      data: {
        constructionGroupGuid: orderCard.constructionGroupGuid,
      },
    });
  };

  useEffect(() => {
    if (addCustomOptionStatus === "ok" && addCustomOptionData) {
      setOrder((prevOrder) =>
        produce(prevOrder, (draft) => {
          if (!draft || !draft.orderCards) return;
          // When we add a card, first we need to update the sort orders of the existing cards
          if (addCustomOptionData.orderCardSort && addCustomOptionData.orderCardSort.length > 0) {
            for(let i=0; i<addCustomOptionData.orderCardSort.length; i++) {
              const dindex = draft.orderCards.findIndex((dcard) => dcard.orderCardGuid === addCustomOptionData.orderCardSort[i].orderCardGuid);
              if (dindex >= 0) draft.orderCards[dindex].sortOrder = addCustomOptionData.orderCardSort[i].sortOrder;
            }
          }

          // Now we should have a hole in the sort order
          const insertAfterIndex = draft.orderCards.findIndex((dcard) => dcard.sortOrder === addCustomOptionData.orderCard.sortOrder-1);
          if (insertAfterIndex >= 0) {
            draft.orderCards.splice(insertAfterIndex+1, 0, addCustomOptionData.orderCard);
          }

          draft.saveAsOptions = addCustomOptionData.saveAsOptions;
        })
      );
    }
    setIsLoading(false);
  }, [addCustomOptionStatus, setIsLoading, addCustomOptionData, setOrder]);

  const { showErrorToast } = useGlobalToastNotificationContext();
  useEffect(() => {
    if (addCustomOptionErrorMessage) {
      showErrorToast({ message: addCustomOptionErrorMessage });
    }
  }, [addCustomOptionErrorMessage, showErrorToast]);
  
  return (
    <>
      {isLoading && <LoadingOverlay />}
      {!(orderCard?.isHidden ?? false) && (
        <OrderCardBase
          disabled={disabled}
          orderCard={orderCard}
          subtitle={orderCardTotal}
          isRequired={orderCard.isRequired}
          onClearClick={handleClearClick}
          expanded={expanded}
          groupIndex={groupIndex}
          action={
          isAddCustomOptionButtonVisible && (
            <OrderCardActionButton
              onClick={handleAddCustomOptionClick}
              disabled={addCustomOptionLoading}
            >
              Custom Option
            </OrderCardActionButton>
          )
        }
        >
          <Grid container spacing={2} component="form">
            <Grid
              xs={isPricePerQuantity ? 6 : 9}
              sm={isPricePerQuantity ? 6 : 9}
              md={isPricePerQuantity ? 6 : 9}
            >
              <Controller
                control={control}
                name="selectedOptionGuid"
                render={({ field, fieldState }) => (
                  <SelectField
                    {...field}
                    label="Options"
                    onFocus={handleEditingField}
                    placeholder="Enter Options"
                    fullWidth
                    disabled={disabled}
                    options={optionsFieldOptions}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    onChange={(e) => {
                      const newSelectedOptionGuid = e.target.value as string;
                      setValue("selectedOptionGuid",newSelectedOptionGuid);
                      setValue("quantity", null);
                      setTimeout(handleBlurField,10);
                      handleBlurField();
                    }}
                  />
                )}
              />
            </Grid>
            {isPricePerQuantity && (
              <Grid xs={3} sm={3} md={3}>
                <InputField
                  {...register("quantity", {
                    valueAsNumber: true,
                    required: "Please enter a quantity.",
                    validate: (v) => (!!v && v > 0) || "Quantity must be greater than 0.",
                    onBlur: () => {
                      handleBlurField();
                    },
                  })}
                  label="Quantity"
                  onFocus={handleEditingField}
                  placeholder="Enter Quantity"
                  fullWidth
                  required={true}
                  disabled={disabled}
                  type="number"
                  intOnly={true}
                  error={!!formState.errors.quantity}
                  helperText={formState.errors.quantity?.message}
                />
              </Grid>
            )}
            <Grid xs={3} sm={3} md={3}>
              <InputField
                label="Price"
                placeholder=""
                required={false}
                fullWidth
                disabled={true}
                type="number"
                startAdornment={
                  <InputAdornment position="start">$ {orderCard.cardData.selectedOptionGuid ? formatWithCommas(isPricePerQuantity ? (selectedOption?.optionPricing[0]?.price ?? 0) : (orderCard.totalPrice ?? 0)) + ".00" : null}</InputAdornment>
                }
                endAdornment={
                  isPricePerQuantity && selectedOption ? (
                    <InputAdornment position="end">/{getConstructionOptionPricePerShortDisplayName("q")}</InputAdornment>
                  ) : undefined
                }
                intOnly={true}
              />
            </Grid>
            <Grid xs={12} sm={12} md={12}>
              <InputField
                {...register("notes", { 
                  onBlur: () => {
                    handleBlurField();
                  }
                })}
                label="Notes"
                onFocus={handleEditingField}
                placeholder="Enter Notes"
                fullWidth
                required={false}
                disabled={disabled}
                type="text"
                minRows={1}
                multiline
                error={!!formState.errors.notes}
                helperText={formState.errors.notes?.message}
              />
            </Grid>
          </Grid>
        </OrderCardBase>
      )}      
    </>
  );
};

const getOptionOption = (option: ConstructionOption, errorText?: string) => {
  return {
    label: (
      <>
        {option.optionName}
        {!!errorText ? (
          <Typography component={"span"} color="error" marginLeft={0.5}>
            {errorText}
          </Typography>
        ) : null}
      </>
    ),
    value: option.constructionOptionGuid,
  };
};
